import { Plus, Trash2, PartyPopper } from "lucide-react";
import { WEEKDAY_CODES, WEEKDAY_LABELS, shiftHours, avgDailyHours, is24hCoverage, newEmptyShift } from "@/lib/contractShifts";
import { Time24Select } from "@/components/Time24Select";

// Editor de "Datos del servicio contratado" — reemplaza el horario único por
// una LISTA de franjas horarias (ej. "Turno día" 08:00-20:00 x1 persona +
// "Turno noche" 20:00-08:00 x2 personas), porque el mismo objetivo puede
// tener distinta dotación de día/noche o semana/fin de semana.
// Prompt AI - Cambio 2: además de los 7 días de semana, una franja puede
// marcarse "Feriado" -- reemplaza al horario normal SOLO en días feriado
// (no en ningún día de semana). "Sin servicio en feriados" (prop opcional)
// fuerza dotación 0 esos días, sin importar las franjas.
export function ContractShiftsEditor({ shifts, onChange, totalOverride, onTotalOverrideChange, testIdPrefix = "shift", readOnly = false, noServiceOnHolidays, onNoServiceOnHolidaysChange }) {
  const list = shifts || [];

  const updateShift = (idx, patch) => {
    const next = list.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(next);
  };

  const toggleDay = (idx, code) => {
    if (readOnly) return;
    const s = list[idx];
    const current = s.days_of_week || [];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    updateShift(idx, { days_of_week: next.length === 0 ? null : next });
  };

  const addShift = () => onChange([...list, newEmptyShift()]);
  const removeShift = (idx) => onChange(list.filter((_, i) => i !== idx));

  const autoTotal = avgDailyHours(list);
  const is24h = is24hCoverage(list);
  const displayedTotal = totalOverride !== "" && totalOverride != null ? Number(totalOverride) : autoTotal;

  return (
    <div data-testid={`${testIdPrefix}-editor`}>
      {!readOnly && (
        <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 10px" }}>
          Agregá una franja por cada turno con dotación distinta (ej. día 1 persona, noche 2 personas). Podés dejar una sola franja si el servicio tiene un solo horario.
        </p>
      )}

      {list.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--invict-text-muted)", padding: "10px 0" }} data-testid={`${testIdPrefix}-empty`}>
          Todavía no cargaste ninguna franja horaria.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {list.map((s, idx) => (
          <div key={s.id || idx} style={{ border: "1.5px solid var(--invict-bone-3)", borderRadius: 6, padding: 10, background: "#fff" }} data-testid={`${testIdPrefix}-row-${idx}`}>
            <div style={{ display: "grid", gridTemplateColumns: readOnly ? "1.4fr 1fr 1fr 0.8fr" : "1.4fr 1fr 1fr 0.8fr auto", gap: 8, alignItems: "end" }}>
              <div className="field" style={{ margin: 0 }}>
                <label>Etiqueta</label>
                <input disabled={readOnly} value={s.label || ""} placeholder="Ej: Turno día" onChange={(e) => updateShift(idx, { label: e.target.value })} data-testid={`${testIdPrefix}-label-${idx}`} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Desde</label>
                <Time24Select disabled={readOnly} value={s.time_from || ""} onChange={(v) => updateShift(idx, { time_from: v })} testId={`${testIdPrefix}-time-from-${idx}`} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Hasta</label>
                <Time24Select disabled={readOnly} value={s.time_to || ""} onChange={(v) => updateShift(idx, { time_to: v })} testId={`${testIdPrefix}-time-to-${idx}`} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Personal</label>
                <input disabled={readOnly} type="number" min={1} value={s.staff_count ?? 1} onChange={(e) => updateShift(idx, { staff_count: e.target.value })} data-testid={`${testIdPrefix}-staff-count-${idx}`} />
              </div>
              {!readOnly && (
                <button type="button" onClick={() => removeShift(idx)} className="btn-ghost" style={{ padding: "8px 10px", color: "var(--invict-red)", borderColor: "var(--invict-red)" }} title="Quitar franja" data-testid={`${testIdPrefix}-remove-${idx}`}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700, textTransform: "uppercase", marginRight: 2 }}>Días:</span>
              {WEEKDAY_CODES.map((code) => {
                const active = !s.days_of_week || s.days_of_week.includes(code);
                const restricted = !!s.days_of_week;
                return (
                  <button key={code} type="button" onClick={() => toggleDay(idx, code)} disabled={readOnly} data-testid={`${testIdPrefix}-day-${code}-${idx}`}
                    style={{ padding: "3px 8px", fontSize: 10, fontWeight: 800, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: readOnly ? "default" : "pointer", background: active ? (restricted ? "#0A0A0A" : "var(--invict-bone-2)") : "#fff", color: active ? (restricted ? "#FFFBF4" : "#0A0A0A") : "var(--invict-text-muted)" }}>
                    {WEEKDAY_LABELS[code]}
                  </button>
                );
              })}
              <button type="button" onClick={() => toggleDay(idx, "feriado")} disabled={readOnly} title="Horario especial para días feriado (reemplaza al horario normal ese día)" data-testid={`${testIdPrefix}-day-feriado-${idx}`}
                style={{ padding: "3px 8px", fontSize: 10, fontWeight: 800, borderRadius: 4, border: "1.5px solid #d4af37", cursor: readOnly ? "default" : "pointer", background: (s.days_of_week || []).includes("feriado") ? "#d4af37" : "#fff", color: (s.days_of_week || []).includes("feriado") ? "#0A0A0A" : "#a67c00", display: "inline-flex", alignItems: "center", gap: 3 }}>
                <PartyPopper size={10} />Feriado
              </button>
              <span style={{ fontSize: 10, color: "var(--invict-text-muted)", marginLeft: 4 }}>{!s.days_of_week ? "(todos los días)" : ""}</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#0A0A0A", marginLeft: "auto" }}>{shiftHours(s)} h/día cubierto</span>
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <button type="button" onClick={addShift} className="btn-ghost" style={{ marginTop: 10, padding: "8px 14px", fontSize: 11 }} data-testid={`${testIdPrefix}-add-btn`}>
          <Plus size={13} style={{ marginRight: 6 }} />Agregar franja horaria
        </button>
      )}

      {onNoServiceOnHolidaysChange && (
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12, fontWeight: 700, color: "#0A0A0A", cursor: readOnly ? "default" : "pointer" }} data-testid={`${testIdPrefix}-no-service-holidays-label`}>
          <input type="checkbox" disabled={readOnly} checked={!!noServiceOnHolidays} onChange={(e) => onNoServiceOnHolidaysChange(e.target.checked)} data-testid={`${testIdPrefix}-no-service-holidays-checkbox`} />
          Sin servicio en feriados (dotación 0 esos días)
        </label>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
        {!readOnly && (
          <div className="field" style={{ margin: 0, maxWidth: 220 }}>
            <label>Total de horas contratadas (por día)</label>
            <input type="number" step="0.5" value={totalOverride ?? ""} placeholder={autoTotal != null ? `Auto: ${autoTotal}` : "Se calcula automático"} onChange={(e) => onTotalOverrideChange(e.target.value)} data-testid={`${testIdPrefix}-total-override-input`} />
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }} data-testid={`${testIdPrefix}-summary-total`}>
          Total: {displayedTotal != null ? `${displayedTotal} h/día` : "—"}
        </div>
        {is24h && (
          <span className="badge green" data-testid={`${testIdPrefix}-summary-24h-badge`}>Cobertura 24 hs</span>
        )}
      </div>
      {!readOnly && <p style={{ fontSize: 10, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>Autocalculado desde las franjas (promedio por día de la semana). Corregilo a mano si el servicio no es continuo.</p>}
    </div>
  );
}
